import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/ui/use-toast";
import { Moon, Sun, ArrowLeft } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Star, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tables } from '@/integrations/supabase/types';
import { trackFunnelStep, generateSessionId, type FunnelStep } from '@/lib/analytics/funnel-tracking';
import { InlineTextEditor } from '@/components/ui/inline-text-editor';

interface ReviewForm {
  id: string;
  business_id: string;
  title: string;
  active_status: boolean;
  created_at: string;
  updated_at: string;
}

interface Question extends Tables<'questions'> {
  options?: { id: string; value: string }[];
}

interface UITextConfig {
  // Step 1 - Rating
  ratingTitle: string;
  ratingSubtitle: string;
  ratingCTA: string;
  
  // Step 2 - Email
  rewardTitle: string;
  feedbackTitle: string;
  emailPlaceholder: string;
  consentText: string;
  continueButtonText: string;
  nextButtonText: string;
  
  // Step 3 - Form
  formTitle: string;
  nameLabel: string;
  namePlaceholder: string;
  submitButtonText: string;
  
  // Step 4 - Success
  successTitle: string;
  successMessageHigh: string;
  successMessageLow: string;
}

const DEFAULT_UI_TEXT: UITextConfig = {
  ratingTitle: "Rate your experience",
  ratingSubtitle: "How would you rate your overall experience?",
  ratingCTA: "Click a star to continue",
  rewardTitle: "Get Your Reward!",
  feedbackTitle: "Tell us more",
  emailPlaceholder: "Enter your email for reward",
  consentText: "I agree to receive promotional emails and rewards",
  continueButtonText: "Continue to Google Reviews",
  nextButtonText: "Next",
  formTitle: "Share Your Feedback",
  nameLabel: "Your Name (Optional)",
  namePlaceholder: "Enter your name",
  submitButtonText: "Submit Feedback",
  successTitle: "Thank You!",
  successMessageHigh: "Your feedback has been submitted and you were redirected to Google Reviews.",
  successMessageLow: "Your feedback has been submitted successfully. We appreciate your input!"
};

// Message types for parent-iframe communication
interface PreviewMessage {
  type: 'UPDATE_UI_TEXT' | 'UPDATE_QUESTION' | 'UPDATE_FORM_DATA' | 'EDIT_REQUEST' | 'QUESTION_EDIT' | 'FORM_INTERACTION';
  data?: any;
  field?: string;
  value?: string;
  questionId?: string;
}

const PreviewReviewForm: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditable = searchParams.get('editable') === 'true';
  const { setTheme } = useTheme();
  
  const [form, setForm] = useState<ReviewForm | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [business, setBusiness] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uiTextConfig, setUITextConfig] = useState<UITextConfig>(DEFAULT_UI_TEXT);
  
  // Form state
  const [currentStep, setCurrentStep] = useState(1); // 1: Rating, 2: Email (if reward), 3: Custom Form, 4: Success
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [answers, setAnswers] = useState<{[key: string]: any}>({});
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [sessionId] = useState(() => generateSessionId());

  // Helper function to handle navigation in iframe context
  const handleNavigation = (path: string) => {
    // Don't navigate away if we're in an iframe (preview mode)
    if (window.self !== window.top) {
      console.log("Navigation blocked in iframe preview mode");
      return;
    }
    navigate(path);
  };

  // Block all navigation in iframe preview mode
  useEffect(() => {
    if (window.self !== window.top) {
      // Override navigate function to prevent navigation
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;
      
      window.history.pushState = (...args) => {
        console.log("pushState blocked in iframe preview mode");
        return;
      };
      
      window.history.replaceState = (...args) => {
        console.log("replaceState blocked in iframe preview mode");
        return;
      };
      
      // Block popstate events
      const handlePopState = (event: PopStateEvent) => {
        event.preventDefault();
        event.stopPropagation();
        console.log("popstate blocked in iframe preview mode");
      };
      
      window.addEventListener('popstate', handlePopState, true);
      
      // Block beforeunload
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = '';
        console.log("beforeunload blocked in iframe preview mode");
        return '';
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
        window.removeEventListener('popstate', handlePopState, true);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, []);

  // Listen for messages from parent window when in editable mode
  useEffect(() => {
    if (!isEditable) return;

    const handleMessage = (event: MessageEvent<PreviewMessage>) => {
      const { type, field, value, data, questionId } = event.data;

      switch (type) {
        case 'UPDATE_UI_TEXT':
          if (field && value !== undefined) {
            setUITextConfig(prev => ({
              ...prev,
              [field]: value
            }));
          }
          break;
        case 'UPDATE_QUESTION':
          if (questionId && data) {
            setQuestions(prev => prev.map(q => 
              q.id === questionId ? { ...q, ...data } : q
            ));
          }
          break;
        case 'UPDATE_FORM_DATA':
          if (data) {
            if (data.questions) setQuestions(data.questions);
            if (data.business) setBusiness(data.business);
            if (data.uiTextConfig) setUITextConfig(data.uiTextConfig);
          }
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isEditable]);

  // Send messages to parent window
  const sendMessageToParent = (message: PreviewMessage) => {
    if (isEditable && window.parent) {
      window.parent.postMessage(message, '*');
    }
  };

  useEffect(() => {
    fetchFormData();
  }, [formId]);

  const fetchFormData = async () => {
    if (!formId) {
      // In iframe mode, don't navigate away, just show error
      if (window.self !== window.top) {
        console.error("Invalid form ID in preview mode");
        setLoading(false);
        setError("Invalid form ID");
        return;
      }
      toast.error("Invalid form ID");
      handleNavigation("/");
      return;
    }

    // Handle mock mode for preview without real formId
    if (formId === 'mock') {
      setLoading(false);
      setForm({
        id: 'mock',
        business_id: 'mock',
        title: 'Review Form',
        active_status: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      setBusiness({
        id: 'mock',
        name: 'Your Business',
        review_redirect_star_threshold: 4,
        reward_enabled: true,
        google_place_id: null
      });
      setQuestions([]);
      return;
    }

    try {
      setLoading(true);

      // Fetch form with business data and questions
      const { data: formData, error: formError } = await supabase
        .from('review_forms')
        .select(`
          *,
          businesses!inner (
            id,
            name,
            review_redirect_star_threshold,
            reward_enabled,
            google_place_id
          ),
          questions (*)
        `)
        .eq('id', formId)
        .eq('active_status', true)
        .single();

      if (formError) {
        if (formError.code === 'PGRST116') {
          toast.error("Form not found or is not active");
        } else {
          throw formError;
        }
        handleNavigation("/");
        return;
      }

      if (!formData) {
        toast.error("Form not found");
        handleNavigation("/");
        return;
      }

      setForm(formData);
      setBusiness(formData.businesses);
      setQuestions(formData.questions || []);
      
      // Load UI text configuration from form or use defaults
      if (formData.ui_text_config) {
        setUITextConfig({ ...DEFAULT_UI_TEXT, ...formData.ui_text_config } as UITextConfig);
      }

      // Track page loaded (only if not in editable preview mode)
      if (!isEditable) {
        trackFunnelStep({
          sessionId,
          businessId: formData.businesses.id,
          stepName: 'page_loaded',
          stepData: { formId, formTitle: formData.title }
        });
      }

    } catch (error: any) {
      console.error("Error fetching form data:", error);
      toast.error("Failed to load form", {
        description: error.message || "Please try again later."
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to process UI text with business name placeholder
  const processUIText = (text: string, businessName: string = "Your Business"): string => {
    return text.replace(/{businessName}/g, business?.name || businessName);
  };

  // Handle UI text editing
  const handleUITextEdit = (field: keyof UITextConfig, value: string) => {
    if (isEditable) {
      sendMessageToParent({
        type: 'EDIT_REQUEST',
        field,
        value
      });
    }
  };

  // Handle question editing
  const handleQuestionEdit = (questionId: string, field: string, value: any) => {
    if (isEditable) {
      sendMessageToParent({
        type: 'QUESTION_EDIT',
        questionId,
        data: { [field]: value }
      });
    }
  };

  // EditableButton component for preview mode
  const EditableButton: React.FC<{
    onClick: () => void;
    className: string;
    textKey: keyof UITextConfig;
    children?: React.ReactNode;
  }> = ({ onClick, className, textKey, children }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(uiTextConfig[textKey]);
    
    // Update edit value when UI text changes
    React.useEffect(() => {
      if (!isEditing) {
        setEditValue(uiTextConfig[textKey]);
      }
    }, [uiTextConfig[textKey], isEditing]);
    
    const handleSave = () => {
      if (editValue.trim() !== uiTextConfig[textKey]) {
        handleUITextEdit(textKey, editValue.trim());
      }
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(uiTextConfig[textKey]);
      setIsEditing(false);
    };

    if (isEditing && isEditable) {
      return (
        <div className="relative w-full">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
            onBlur={handleSave}
            className="w-full bg-white/90 text-gray-900 py-2.5 px-3 rounded-lg text-sm font-medium border-2 border-blue-500 focus:outline-none"
            autoFocus
          />
        </div>
      );
    }

    return (
      <div className="relative group">
        <button onClick={onClick} className={className}>
          {uiTextConfig[textKey]}
          {children}
        </button>
        {isEditable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
              setEditValue(uiTextConfig[textKey]);
            }}
            className="absolute top-1 right-1 w-5 h-5 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
            title="Edit button text"
          >
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const handleRatingClick = (selectedRating: number) => {
    setRating(selectedRating);
    const threshold = business?.review_redirect_star_threshold || 4;
    
    // Send interaction to parent for preview purposes
    if (isEditable) {
      sendMessageToParent({
        type: 'FORM_INTERACTION',
        data: { step: 1, action: 'rating_click', rating: selectedRating }
      });
    }
    
    // Track rating selection (only if not in editable preview mode)
    if (!isEditable) {
      trackFunnelStep({
        sessionId,
        businessId: business.id,
        stepName: 'rating_selected',
        stepData: { rating: selectedRating, threshold }
      });
    }
    
    if (selectedRating >= threshold) {
      // High rating flow - redirect to Google Reviews if available
      if (business?.google_place_id && !isEditable) {
        // Track Google redirect
        trackFunnelStep({
          sessionId,
          businessId: business.id,
          stepName: 'redirected_to_google',
          stepData: { rating: selectedRating }
        });
        
        // Redirect to Google Reviews
        const googleUrl = `https://search.google.com/local/writereview?placeid=${business.google_place_id}`;
        window.open(googleUrl, '_blank');
        setCurrentStep(4); // Show success message
        return;
      } else {
        // No Google Place ID or in preview mode, proceed to form
        if (business?.reward_enabled) {
          // Track email step shown
          if (!isEditable) {
            trackFunnelStep({
              sessionId,
              businessId: business.id,
              stepName: 'email_step_shown',
              stepData: { rating: selectedRating }
            });
          }
          setCurrentStep(2); // Go to email step
        } else {
          // Track feedback form shown
          if (!isEditable) {
            trackFunnelStep({
              sessionId,
              businessId: business.id,
              stepName: 'feedback_form_shown',
              stepData: { rating: selectedRating }
            });
          }
          setCurrentStep(3); // Go directly to form
        }
      }
    } else {
      // Low rating flow
      if (business?.reward_enabled) {
        // Track email step shown for low rating
        if (!isEditable) {
          trackFunnelStep({
            sessionId,
            businessId: business.id,
            stepName: 'email_step_shown',
            stepData: { rating: selectedRating }
          });
        }
        setCurrentStep(2); // Go to email step
      } else {
        // Track feedback form shown for low rating
        if (!isEditable) {
          trackFunnelStep({
            sessionId,
            businessId: business.id,
            stepName: 'feedback_form_shown',
            stepData: { rating: selectedRating }
          });
        }
        setCurrentStep(3); // Go directly to form
      }
    }
  };

  const handleEmailStep = () => {
    if (rating >= (business?.review_redirect_star_threshold || 4)) {
      setCurrentStep(4); // Success (would have redirected to Google)
    } else {
      setCurrentStep(3); // Go to custom form
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Clear error when user starts typing
    if (errors[questionId]) {
      setErrors(prev => ({
        ...prev,
        [questionId]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    questions.forEach(question => {
      if (question.required) {
        const answer = answers[question.id];
        if (!answer || (typeof answer === 'string' && !answer.trim())) {
          newErrors[question.id] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // In preview mode, just show success
    if (isEditable) {
      setCurrentStep(4);
      return;
    }

    setSubmitting(true);

    try {
      // Create review entry
      const { data: reviewData, error: reviewError } = await supabase
        .from('reviews')
        .insert({
          business_id: business.id,
          customer_name: customerName.trim() || null,
          text: '', // We'll build this from custom answers
          rating: rating || 5, // Default to 5 if no rating question
          source: 'Custom Form',
          custom_answers: answers, // Store all custom answers
          form_id: formId // Link to the form used
        })
        .select()
        .single();

      if (reviewError) throw reviewError;

      // Track review submission
      trackFunnelStep({
        sessionId,
        businessId: business.id,
        stepName: 'review_submitted',
        stepData: { 
          rating: rating || 5,
          hasCustomAnswers: Object.keys(answers).length > 0,
          customerName: customerName.trim() || null
        }
      });

      toast.success("Thank you for your feedback!", {
        description: "Your review has been submitted successfully."
      });

      // Move to success step
      setCurrentStep(4);

    } catch (error: any) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review", {
        description: error.message || "Please try again."
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: Question) => {
    const error = errors[question.id];
    
    switch (question.type) {
      case 'text':
        return (
          <div key={question.id} className="space-y-2">
            <Label htmlFor={question.id}>
              {isEditable ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={question.id}
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder="Your answer..."
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={question.id} className="space-y-2">
            <Label htmlFor={question.id}>
              {isEditable ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id={question.id}
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder="Your detailed response..."
              rows={4}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'rating':
        return (
          <div key={question.id} className="space-y-2">
            <Label>
              {isEditable ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex justify-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={32}
                  className={`cursor-pointer transition-all duration-200 ${
                    (answers[question.id] >= star || hoveredRating >= star)
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300 hover:text-yellow-200"
                  }`}
                  onClick={() => {
                    handleAnswerChange(question.id, star);
                    setRating(star); // Also set main rating
                  }}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                />
              ))}
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'multiple_choice':
        return (
          <div key={question.id} className="space-y-2">
            <Label>
              {isEditable ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={answers[question.id] || ''}
              onValueChange={(value) => handleAnswerChange(question.id, value)}
            >
              {(question.options || []).map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value} id={`${question.id}-${option.id}`} />
                  <Label htmlFor={`${question.id}-${option.id}`}>
                    {isEditable ? (
                      <InlineTextEditor
                        value={option.value}
                        onSave={(newValue) => {
                          // Update option value
                          const updatedOptions = (question.options || []).map(opt =>
                            opt.id === option.id ? { ...opt, value: newValue } : opt
                          );
                          handleQuestionEdit(question.id, 'options', updatedOptions);
                        }}
                        placeholder="Option text"
                        className="text-sm"
                      />
                    ) : (
                      option.value
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'checkboxes':
        return (
          <div key={question.id} className="space-y-2">
            <Label>
              {isEditable ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {(question.options || []).map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${question.id}-${option.id}`}
                    checked={(answers[question.id] || []).includes(option.value)}
                    onCheckedChange={(checked) => {
                      const currentAnswers = answers[question.id] || [];
                      if (checked) {
                        handleAnswerChange(question.id, [...currentAnswers, option.value]);
                      } else {
                        handleAnswerChange(question.id, currentAnswers.filter((v: string) => v !== option.value));
                      }
                    }}
                  />
                  <Label htmlFor={`${question.id}-${option.id}`}>
                    {isEditable ? (
                      <InlineTextEditor
                        value={option.value}
                        onSave={(newValue) => {
                          // Update option value
                          const updatedOptions = (question.options || []).map(opt =>
                            opt.id === option.id ? { ...opt, value: newValue } : opt
                          );
                          handleQuestionEdit(question.id, 'options', updatedOptions);
                        }}
                        placeholder="Option text"
                        className="text-sm"
                      />
                    ) : (
                      option.value
                    )}
                  </Label>
                </div>
              ))}
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <div className="text-lg font-medium">Loading form...</div>
        </div>
      </div>
    );
  }

  if (!form || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-lg font-medium mb-2">Form not found</div>
            <p className="text-muted-foreground mb-4">
              This form may have been deactivated or removed.
            </p>
            <Button onClick={() => handleNavigation("/")} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header with theme toggle - hidden in editable mode */}
      {!isEditable && (
        <div className="flex justify-between items-center p-4 border-b">
          <Button 
            variant="ghost" 
            onClick={() => handleNavigation("/")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-10 w-10">
                <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span className="sr-only">Toggle theme</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setTheme('light')}>Light</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>Dark</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')}>System</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Main Content */}
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <Card>
          <CardContent className="pt-6">
            {/* Step 1: Rating */}
            {currentStep === 1 && (
              <div className="text-center space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    {isEditable ? (
                      <InlineTextEditor
                        value={uiTextConfig.ratingTitle}
                        onSave={(newText) => handleUITextEdit('ratingTitle', newText)}
                        placeholder="Rate your experience"
                        className="text-xl font-semibold"
                      />
                    ) : (
                      uiTextConfig.ratingTitle
                    )}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {isEditable ? (
                      <InlineTextEditor
                        value={uiTextConfig.ratingSubtitle}
                        onSave={(newText) => handleUITextEdit('ratingSubtitle', newText)}
                        placeholder="How would you rate your overall experience?"
                        className="text-muted-foreground"
                      />
                    ) : (
                      processUIText(uiTextConfig.ratingSubtitle, business.name)
                    )}
                  </p>
                </div>
                
                <div className="flex justify-center space-x-2 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={48}
                      className={`cursor-pointer transition-all duration-200 ${
                        (rating >= star || hoveredRating >= star)
                          ? "text-yellow-400 fill-yellow-400 scale-110"
                          : "text-gray-300 hover:text-yellow-200"
                      }`}
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  {isEditable ? (
                    <InlineTextEditor
                      value={uiTextConfig.ratingCTA}
                      onSave={(newText) => handleUITextEdit('ratingCTA', newText)}
                      placeholder="Click a star to continue"
                      className="text-sm text-muted-foreground"
                    />
                  ) : (
                    uiTextConfig.ratingCTA
                  )}
                </p>
              </div>
            )}

            {/* Step 2: Email Collection (if reward enabled) */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-2">
                    {isEditable ? (
                      <InlineTextEditor
                        value={rating >= (business?.review_redirect_star_threshold || 4) ? uiTextConfig.rewardTitle : uiTextConfig.feedbackTitle}
                        onSave={(newText) => handleUITextEdit(rating >= (business?.review_redirect_star_threshold || 4) ? 'rewardTitle' : 'feedbackTitle', newText)}
                        placeholder={rating >= (business?.review_redirect_star_threshold || 4) ? 'Get Your Reward!' : 'Tell us more'}
                        className="text-xl font-semibold"
                      />
                    ) : (
                      rating >= (business?.review_redirect_star_threshold || 4) ? uiTextConfig.rewardTitle : uiTextConfig.feedbackTitle
                    )}
                  </h3>
                  <p className="text-muted-foreground">
                    {rating >= (business?.review_redirect_star_threshold || 4) 
                      ? 'Thank you for the great rating! Enter your email to receive your reward.'
                      : 'We\'d like to learn more about your experience.'
                    }
                  </p>
                </div>
                
                {business?.reward_enabled && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="customer-email">Email Address</Label>
                      <Input
                        id="customer-email"
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder={isEditable ? uiTextConfig.emailPlaceholder : uiTextConfig.emailPlaceholder}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-start space-x-2">
                      <Checkbox id="email-consent" className="mt-0.5" />
                      <Label htmlFor="email-consent" className="text-sm text-muted-foreground">
                        {isEditable ? (
                          <InlineTextEditor
                            value={uiTextConfig.consentText}
                            onSave={(newText) => handleUITextEdit('consentText', newText)}
                            placeholder="I agree to receive promotional emails and rewards"
                            className="text-sm text-muted-foreground"
                          />
                        ) : (
                          uiTextConfig.consentText
                        )}
                      </Label>
                    </div>
                  </div>
                )}
                
                <EditableButton
                  onClick={handleEmailStep}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8 rounded-md text-sm font-medium"
                  textKey={rating >= (business?.review_redirect_star_threshold || 4) ? 'continueButtonText' : 'nextButtonText'}
                />
              </div>
            )}

            {/* Step 3: Custom Form */}
            {currentStep === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold mb-2">
                    {isEditable ? (
                      <InlineTextEditor
                        value={uiTextConfig.formTitle}
                        onSave={(newText) => handleUITextEdit('formTitle', newText)}
                        placeholder="Share Your Feedback"
                        className="text-xl font-semibold"
                      />
                    ) : (
                      uiTextConfig.formTitle
                    )}
                  </h3>
                  <p className="text-muted-foreground">Help us improve by answering a few questions</p>
                </div>

                {/* Customer Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="customer-name">
                    {isEditable ? (
                      <InlineTextEditor
                        value={uiTextConfig.nameLabel}
                        onSave={(newText) => handleUITextEdit('nameLabel', newText)}
                        placeholder="Your Name (Optional)"
                        className="text-sm font-medium"
                      />
                    ) : (
                      uiTextConfig.nameLabel
                    )}
                  </Label>
                  <Input
                    id="customer-name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder={uiTextConfig.namePlaceholder}
                  />
                </div>

                {/* Custom Questions */}
                {questions.map(renderQuestion)}

                {/* Submit Button */}
                <div className="pt-4">
                  <EditableButton
                    onClick={() => {}}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8 rounded-md text-sm font-medium disabled:pointer-events-none disabled:opacity-50"
                    textKey="submitButtonText"
                  >
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  </EditableButton>
                </div>
              </form>
            )}

            {/* Step 4: Success */}
            {currentStep === 4 && (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <div className="text-3xl">✅</div>
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  {isEditable ? (
                    <InlineTextEditor
                      value={uiTextConfig.successTitle}
                      onSave={(newText) => handleUITextEdit('successTitle', newText)}
                      placeholder="Thank You!"
                      className="text-xl font-semibold"
                    />
                  ) : (
                    uiTextConfig.successTitle
                  )}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {isEditable ? (
                    <InlineTextEditor
                      value={rating >= (business?.review_redirect_star_threshold || 4) ? uiTextConfig.successMessageHigh : uiTextConfig.successMessageLow}
                      onSave={(newText) => handleUITextEdit(rating >= (business?.review_redirect_star_threshold || 4) ? 'successMessageHigh' : 'successMessageLow', newText)}
                      placeholder={rating >= (business?.review_redirect_star_threshold || 4) ? 'Your feedback has been submitted and you were redirected to Google Reviews.' : 'Your feedback has been submitted successfully. We appreciate your input!'}
                      className="text-muted-foreground"
                    />
                  ) : (
                    rating >= (business?.review_redirect_star_threshold || 4) 
                      ? uiTextConfig.successMessageHigh
                      : uiTextConfig.successMessageLow
                  )}
                </p>
                {isEditable && (
                  <Button
                    onClick={() => {
                      setCurrentStep(1);
                      setRating(0);
                      setCustomerName('');
                      setCustomerEmail('');
                      setAnswers({});
                      setErrors({});
                    }}
                    variant="outline"
                  >
                    Try Again (Preview Only)
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PreviewReviewForm;